import PublishPopoverComponent from "@/components/common-components/publish-popover/PublishPopoverComponent.tsx";
import UrlSearchBarComponent from "@/components/common-components/url-search-bar/UrlSearchBarComponent.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Dialog, DialogContent } from "@/components/ui/dialog.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { ImageConfig } from "@/constants";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import { AppType } from "@/models/project.model.ts";
import { setEditMode, setIframeUrl, setSelectedHtmlFile } from "@/store/actions/projectdata.action.ts";
import { IRootReducerState } from "@/store/reducers";
import { Code, Edit3Icon, Monitor, PanelLeftIcon, ChevronLeft } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CommonService from "../../../../../../../services/common.service.ts";
import HtmlFileSelectorPopup from "../app-code-and-preview/preview-renderer/sandpack-html-preview/components/HtmlFileSelectorPopup";
import "./AppTabsHeaderBarComponent.scss";
import CreditsPopoverComponent from "@/components/common-components/credits-popover/CreditsPopoverComponent";
import CommitPopupComponent from "../app-tabs-header/commit-popup/CommitPopupComponent.tsx";
import commonService from '@/services/common.service.ts';

interface AppTabsHeaderBarComponentProps {
    onSupobaseToolOpen: () => void;
    currentAppConfiguration: string[];
    projectId: string;
    appType: AppType;
    activeTab: string;
    onTabChange: (tab: string) => void;
    isChatCollapsed: boolean;
    onExpandChat: () => void;
}

const AppTabsHeaderBarComponent = (props: AppTabsHeaderBarComponentProps) => {
    const { onSupobaseToolOpen, projectId, appType, activeTab, onTabChange, isChatCollapsed, onExpandChat } = props;
    const isMobile = useIsMobile();

    const [isCreateModelOpen, setIsCreateModelOpen] = useState(false)
    const [isUpdatingServer, setIsUpdatingServer] = useState<boolean>(false);
    const [isHtmlFileSelectorOpen, setIsHtmlFileSelectorOpen] = useState<boolean>(false);

    const { currentProjectDetails, tempCode, code, iframeUrl } = useSelector((state: IRootReducerState) => state.projectData);
    const isEditMode = useSelector((state: IRootReducerState) => state.projectData.isEditMode);
    const dispatch = useDispatch();

    const handleSetActiveSession = useCallback(() => {
        CommonService.handleMixpanelTrack("Reload preview button clicked", {
            project_id: currentProjectDetails?.id,
            app_type: appType,
        });
        commonService._communications.setPreviewLoadingStateSubject.next(true);
        // For HTML apps, trigger Sandpack reload instead of server session
        if (appType === 'html') {
            setIsUpdatingServer(true);
            CommonService._communications.reloadPreviewSubject.next("reload");

            setTimeout(() => {
                setIsUpdatingServer(false);
            }, 2000);
        } else {
            setIsUpdatingServer(true);
            CommonService._project_service.revertToASession(currentProjectDetails?.id || "", {})
                .then(() => { })
                .catch((error: any) => {
                    CommonService._alert.showToast(error?.detail ?? "Error reloading the preview. Please try again later.", "error");
                })
                .finally(() => {
                    setTimeout(() => {
                        setIsUpdatingServer(false);
                    }, 3000);
                });
        }
    }, [appType, currentProjectDetails?.id]);

    useEffect(() => {
        const sub = CommonService._communications.reloadPreviewSubject.subscribe((data: string | null) => {
            if (data === "reload") {
                handleSetActiveSession();
            }
        });
        return () => {
            sub.unsubscribe();
        };
    }, [handleSetActiveSession]);

    const handleEditModeToggle = useCallback(() => {
        if (isEditMode) {
            dispatch(setEditMode(false));
        } else {
            dispatch(setEditMode(true));
        }
    }, [isEditMode, dispatch]);

    const handleFileSelect = useCallback((file: any) => {
        dispatch(setSelectedHtmlFile(file));
        setIsHtmlFileSelectorOpen(false);
    }, [dispatch]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.ctrlKey || event.metaKey) && event.key === 's') {
                event.preventDefault();
                if (activeTab === "code" && tempCode !== code) {
                    setIsCreateModelOpen(true);
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [activeTab, tempCode, code]);

    useEffect(() => {
        const sub = CommonService._communications.triggerCommitDialogSubject.subscribe((shouldOpen: boolean) => {
            if (shouldOpen && activeTab === "code" && tempCode !== code) {
                CommonService.handleMixpanelTrack("Commit dialog opened", {
                    project_id: currentProjectDetails?.id,
                    app_type: appType,
                });
                setIsCreateModelOpen(true);
            }
        });
        return () => {
            sub.unsubscribe();
        };
    }, [activeTab, tempCode, code, currentProjectDetails?.id, appType]);

    const handleNavigateToRoute = useCallback((route: string) => {
        const fullUrl = (iframeUrl || "") + (route.startsWith('/') ? route : `/${route}`);
        dispatch(setIframeUrl(fullUrl));
    }, [iframeUrl, dispatch]);

    return (
        <>
            <div className="app-tabs-header-bar">
                {isMobile && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => CommonService._communications.switchToChatTabSubject.next()}
                        className="h-8 w-8 p-0 -ml-1"
                    >
                        <ChevronLeft size={20} />
                    </Button>
                )}
                {!isMobile && (
                    <div className="flex items-center gap-2">
                        {isChatCollapsed && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={onExpandChat}
                                        mixpanelEvent={{ name: "expand_chat_clicked" }}
                                        className="h-8 w-8 p-0"
                                    >
                                        <PanelLeftIcon className="h-4 w-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Expand Chat</p>
                                </TooltipContent>
                            </Tooltip>
                        )}
                        <Button
                            variant={activeTab === "preview" ? "default" : "outline"}
                            size="sm"
                            onClick={() => onTabChange("preview")}
                            className="h-8 w-8 p-0"
                        >
                            <Monitor size={16} />
                        </Button>
                        <Button
                            variant={activeTab === "code" ? "default" : "outline"}
                            size="sm"
                            onClick={() => onTabChange("code")}
                            className="h-8 w-8 p-0"
                        >
                            <Code size={16} />
                        </Button>
                    </div>
                )}

                {/* Center - URL Search Bar */}
                {activeTab === "preview" && (
                    <div className={`flex-1 ${isMobile ? 'max-w-xs mx-2' : 'max-w-md mx-4'}`}>
                        <UrlSearchBarComponent
                            baseUrl={iframeUrl || ""}
                            onReload={handleSetActiveSession}
                            onNavigateToRoute={(route) => {
                                handleNavigateToRoute(route);
                            }}
                            isLoading={isUpdatingServer}
                            className="w-full"
                        />
                    </div>
                )}

                {/* Right side actions */}
                <div className={`flex items-center ${isMobile ? 'gap-1.5' : 'gap-2'}`}>
                    {/* Desktop-only Credits Popover */}
                    {!isMobile && (
                        <CreditsPopoverComponent />
                    )}


                    {/* Desktop actions */}
                    {!isMobile && (
                        <>
                            {/* Expand Chat Button - only show when chat is collapsed */}
                            {isChatCollapsed && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={onExpandChat}
                                            mixpanelEvent={{ name: "expand_chat_clicked" }}
                                            className="h-8 w-8 p-0"

                                        >
                                            <PanelLeftIcon className="h-4 w-4" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Expand Chat</p>
                                    </TooltipContent>
                                </Tooltip>
                            )}

                            {
                                activeTab === "code" && tempCode !== code &&
                                <Button
                                    type="button"
                                    className="mrg-bottom-5"
                                    onClick={() => { setIsCreateModelOpen(true) }}
                                    size="sm"
                                    variant="outline"
                                    fullWidth={false}
                                    mixpanelEvent={{ name: "save_changes_button_clicked" }}
                                >
                                    Save Changes
                                </Button>
                            }

                            {activeTab === "preview" &&
                                <>
                                    {
                                        appType === 'html' && code &&

                                        <Button
                                            className={` mrg-bottom-5 mrg-right-10`}
                                            size="sm"
                                            color={isEditMode ? "primary" : "secondary"}
                                            variant="outline"
                                            onClick={handleEditModeToggle}
                                            prefixIcon={<Edit3Icon className={'small'} />}
                                            mixpanelEvent={{ name: "edit_mode_toggle_clicked" }}
                                        >
                                            {isEditMode ? "Preview" : "Edit"}
                                        </Button>
                                    }

                                </>
                            }

                            {appType === 'nextjs' && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={onSupobaseToolOpen}
                                            mixpanelEvent={{ name: "manage_supabase_button_clicked" }}
                                        >
                                            <ImageConfig.SupabaseLogoIcon className="h-4 w-4" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Manage Supabase</p>
                                    </TooltipContent>
                                </Tooltip>
                            )}

                            {/* Desktop Publish/Update Popover */}
                            <PublishPopoverComponent
                                projectId={projectId}
                                appType={appType}
                                onPublishSuccess={() => {
                                    // Optional: Add any additional success handling
                                }}
                            />
                        </>
                    )}
                </div>
            </div>

            <Dialog
                open={isCreateModelOpen}
                onOpenChange={() => {
                    setIsCreateModelOpen(false)
                }}
            >
                <DialogContent>
                    <CommitPopupComponent
                        onCodeSave={() => {
                            setIsCreateModelOpen(false)
                        }} projectId={currentProjectDetails?.id}
                        onClose={() => {
                            setIsCreateModelOpen(false)
                        }}
                    />
                </DialogContent>
            </Dialog>
            <HtmlFileSelectorPopup
                isOpen={isHtmlFileSelectorOpen}
                onClose={() => setIsHtmlFileSelectorOpen(false)}
                onFileSelect={handleFileSelect}
            />
        </>
    );
};

export default AppTabsHeaderBarComponent;
